import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import CookieBanner from "@/components/CookieBanner";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

// Public marketing site
import Home from "@/pages/Home";
import Orientacion from "@/pages/Orientacion";
import OrientacionReservar from "@/pages/OrientacionReservar";
import Consultoria from "@/pages/Consultoria";
import ConsultoriaForm from "@/pages/ConsultoriaForm";
import ConsultoriaSuccess from "@/pages/ConsultoriaSuccess";
import InformePremium from "@/pages/InformePremium";
import InformePremiumForm from "@/pages/InformePremiumForm";
import PostSubasta from "@/pages/PostSubasta";
import Contacto from "@/pages/Contacto";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Subastas from "@/pages/Subastas";
import SubastaDetail from "@/pages/SubastaDetail";
import ProvinciaIndex from "@/pages/ProvinciaIndex";
import { isProvinciaSlug } from "@/utils/provincias";
import AdminBlog from "@/pages/AdminBlog";
import AdminSEO from "@/pages/AdminSEO";
import AdminSubastas from "@/pages/AdminSubastas";
import AvisoLegal from "@/pages/AvisoLegal";
import Privacidad from "@/pages/Privacidad";
import Cookies from "@/pages/Cookies";
import Condiciones from "@/pages/Condiciones";

// Admin (internal SaaS tool)
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AdminValoraciones from "@/pages/AdminValoraciones";
import ValoracionCaseDetail from "@/pages/ValoracionCaseDetail";
import ReportView from "@/pages/ReportView";
import PaymentSuccess from "@/pages/PaymentSuccess";
import CRM from "@/pages/CRM";
import CRMClientes from "@/pages/CRMClientes";
import CRMCardDetail from "@/pages/CRMCardDetail";
import CRMCardNew from "@/pages/CRMCardNew";
import CRMCalendar from "@/pages/CRMCalendar";

function AdminAuthRedirect({ children }) {
  const { user, booted } = useAuth();
  if (!booted) return null;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
}

/**
 * Dispatcher para la URL `/subastas/:slug`.
 *
 * - Si `slug` es una provincia conocida (e.g. "madrid", "barcelona", "bizkaia",
 *   "a-coruna"…), renderiza `ProvinciaIndex` (página índice SEO).
 * - Si no, lo trata como identificador/slug de subasta y renderiza
 *   `SubastaDetail`. Esto mantiene retro-compatibilidad con URLs antiguas
 *   tipo `/subastas/SUB-XX-2026-...`.
 */
function SubastasSlugDispatcher() {
  const { id } = useParams();
  if (isProvinciaSlug(id)) return <ProvinciaIndex />;
  return <SubastaDetail />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            {/* ============ PUBLIC SITE ============ */}
            <Route path="/" element={<Home />} />
            <Route path="/orientacion" element={<Orientacion />} />
            <Route path="/orientacion/reservar" element={<OrientacionReservar />} />
            <Route path="/consultoria" element={<Consultoria />} />
            <Route path="/consultoria/contratar" element={<ConsultoriaForm />} />
            <Route path="/consultoria/exito" element={<ConsultoriaSuccess />} />
            <Route path="/informe-premium" element={<InformePremium />} />
            <Route path="/informe-premium/solicitar" element={<InformePremiumForm />} />
            <Route path="/gestion-postsubasta" element={<PostSubasta />} />
            <Route path="/contacto" element={<Contacto />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/subastas" element={<Subastas />} />
            <Route path="/subastas/:id" element={<SubastasSlugDispatcher />} />
            <Route path="/subastas/:loc/:slug" element={<SubastaDetail />} />
            <Route path="/aviso-legal" element={<AvisoLegal />} />
            <Route path="/politica-privacidad" element={<Privacidad />} />
            <Route path="/politica-cookies" element={<Cookies />} />
            <Route path="/condiciones-contratacion" element={<Condiciones />} />

            {/* ============ ADMIN INTERNAL ============ */}
            <Route path="/admin/login" element={<AdminAuthRedirect><Login /></AdminAuthRedirect>} />
            <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin/valoraciones" element={<ProtectedRoute><AdminValoraciones /></ProtectedRoute>} />
            <Route path="/admin/valoraciones/:case_id" element={<ProtectedRoute><ValoracionCaseDetail /></ProtectedRoute>} />
            <Route path="/admin/report/:id" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
            <Route path="/admin/pago/exito" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/admin/crm" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
            <Route path="/admin/crm/clientes" element={<ProtectedRoute><CRMClientes /></ProtectedRoute>} />
            <Route path="/admin/crm/calendario" element={<ProtectedRoute><CRMCalendar /></ProtectedRoute>} />
            <Route path="/admin/crm/nueva" element={<ProtectedRoute><CRMCardNew /></ProtectedRoute>} />
            <Route path="/admin/crm/:id" element={<ProtectedRoute><CRMCardDetail /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/seo" element={<ProtectedRoute><AdminSEO /></ProtectedRoute>} />
            <Route path="/admin/subastas" element={<ProtectedRoute><AdminSubastas /></ProtectedRoute>} />

            {/* ============ LEGACY REDIRECTS ============ */}
            <Route path="/admin/generate" element={<Navigate to="/admin/valoraciones" replace />} />
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            <Route path="/planes" element={<Navigate to="/" replace />} />
            <Route path="/generate" element={<Navigate to="/admin/valoraciones" replace />} />
            <Route path="/report/:id" element={<LegacyReportRedirect />} />
            <Route path="/pago/exito" element={<Navigate to="/admin/pago/exito" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <FloatingWhatsApp />
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function LegacyReportRedirect() {
  const path = window.location.pathname.replace("/report/", "/admin/report/") + window.location.search;
  return <Navigate to={path} replace />;
}

export default App;
